import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import {
    getLastPointPacketIdentity,
    resolveDeviceOnlineFromPoll,
} from '../lib/connectionStatus';
import {
    isTransientNetworkError,
    runWithConcurrencyLimit,
} from '../lib/httpUtils';
import { getSessionEpoch, isSessionActive, subscribeSessionActive } from '../lib/sessionActivity';

const POLL_INTERVAL_MS = 2000;
const DEVICES_PER_TICK = 8;
const MAX_CONCURRENT_REQUESTS = 4;
const DEFAULT_WORKER_URL = `${process.env.PUBLIC_URL || ''}/deviceOnlinePollWorker.js`;

function getNextBatch(devices, cursor, batchSize) {
    if (!devices.length) {
        return { batch: [], nextCursor: 0 };
    }

    const size = Math.min(batchSize, devices.length);
    const batch = [];

    for (let index = 0; index < size; index += 1) {
        batch.push(devices[(cursor + index) % devices.length]);
    }

    return {
        batch,
        nextCursor: (cursor + size) % devices.length,
    };
}

function startPollTimer(onTick, workerUrl) {
    let worker = null;
    let intervalId = null;

    const stop = () => {
        if (worker) {
            try {
                worker.terminate();
            } catch (error) {
                // ignore
            }
            worker = null;
        }

        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
    };

    const startFallbackInterval = () => {
        if (!intervalId) {
            intervalId = setInterval(onTick, POLL_INTERVAL_MS);
        }
    };

    if (typeof Worker !== 'undefined' && workerUrl) {
        try {
            worker = new Worker(workerUrl);
            worker.postMessage({ type: 'configure', intervalMs: POLL_INTERVAL_MS });
            worker.onmessage = () => {
                onTick();
            };
            worker.onerror = () => {
                try {
                    worker.terminate();
                } catch (error) {
                    // ignore
                }
                worker = null;
                startFallbackInterval();
            };

            return stop;
        } catch (error) {
            worker = null;
        }
    }

    intervalId = setInterval(onTick, POLL_INTERVAL_MS);

    return stop;
}

export default function useDeviceOnlineStatus(htkBackend, devices, options = {}) {
    const {
        enabled = true,
        workerUrl = DEFAULT_WORKER_URL,
    } = options;

    const [onlineStatus, setOnlineStatus] = useState({});
    const apiClientRef = useRef(null);
    const deviceStateRef = useRef({});
    const devicesRef = useRef(devices);
    const pollCursorRef = useRef(0);
    const isFetchingRef = useRef(false);
    const deviceSnKey = useMemo(
        () => devices.map((device) => device.sn).filter(Boolean).join('|'),
        [devices]
    );

    devicesRef.current = devices;

    useEffect(() => {
        apiClientRef.current = axios.create({
            baseURL: htkBackend,
            withCredentials: true,
        });
    }, [htkBackend]);

    useEffect(() => {
        if (!enabled || !htkBackend || !deviceSnKey) {
            setOnlineStatus({});
            deviceStateRef.current = {};
            pollCursorRef.current = 0;

            return undefined;
        }

        const deviceKeys = deviceSnKey.split('|').filter(Boolean);
        const nextDeviceState = {};

        deviceKeys.forEach((sn) => {
            if (deviceStateRef.current[sn]) {
                nextDeviceState[sn] = deviceStateRef.current[sn];
            }
        });
        deviceStateRef.current = nextDeviceState;
        pollCursorRef.current = 0;

        setOnlineStatus((prev) => {
            const next = {};
            deviceKeys.forEach((sn) => {
                next[sn] = prev[sn] === true;
            });
            return next;
        });

        const abortController = new AbortController();

        const pollNextBatch = async () => {
            if (!isSessionActive() || isFetchingRef.current || abortController.signal.aborted) {
                return;
            }

            const client = apiClientRef.current;
            if (!client) {
                return;
            }

            const currentDevices = devicesRef.current.filter((device) => device?.sn);
            if (!currentDevices.length) {
                return;
            }

            const { batch, nextCursor } = getNextBatch(
                currentDevices,
                pollCursorRef.current,
                DEVICES_PER_TICK
            );
            pollCursorRef.current = nextCursor;

            isFetchingRef.current = true;
            const pollEpoch = getSessionEpoch();

            const updates = {};

            try {
                await runWithConcurrencyLimit(
                    batch,
                    MAX_CONCURRENT_REQUESTS,
                    async (device) => {
                        if (abortController.signal.aborted) {
                            return;
                        }

                        try {
                            const response = await client.get(
                                `/datalive/lastPoint/${encodeURIComponent(device.sn)}`,
                                { signal: abortController.signal }
                            );
                            if (getSessionEpoch() !== pollEpoch || !isSessionActive() || abortController.signal.aborted) {
                                return;
                            }

                            const currentPacket = getLastPointPacketIdentity(response.data);
                            const previousState = deviceStateRef.current[device.sn];
                            const result = resolveDeviceOnlineFromPoll({
                                previousPacket: previousState?.lastPacket ?? null,
                                currentPacket,
                                lastActivityAt: previousState?.lastActivityAt ?? null,
                            });

                            deviceStateRef.current[device.sn] = {
                                lastPacket: result.lastPacket,
                                lastActivityAt: result.lastActivityAt,
                            };
                            updates[device.sn] = result.isOnline;
                        } catch (error) {
                            if (abortController.signal.aborted || isTransientNetworkError(error)) {
                                return;
                            }

                            delete deviceStateRef.current[device.sn];
                            updates[device.sn] = false;
                        }
                    },
                    abortController.signal
                );

                if (!abortController.signal.aborted && isSessionActive() && getSessionEpoch() === pollEpoch && Object.keys(updates).length > 0) {
                    setOnlineStatus((prev) => ({ ...prev, ...updates }));
                }
            } finally {
                isFetchingRef.current = false;
            }
        };

        pollNextBatch();
        const stopPollTimer = startPollTimer(pollNextBatch, workerUrl);
        const stopSessionWatch = subscribeSessionActive((active) => {
            if (active) {
                pollNextBatch();
            }
        });

        return () => {
            stopSessionWatch();
            abortController.abort();
            stopPollTimer();
            isFetchingRef.current = false;
        };
    }, [enabled, htkBackend, deviceSnKey, workerUrl]);

    return onlineStatus;
}
